import React, { FC } from 'react'
import { getValueListBgColor } from '../../../../../utils/background-util'
import {
  getCWPEnvironmentForURL,
  APIENDPOINT,
  sanitizeHTML,
} from '../../../../../utils/common-utils'
import { TwoColumnBannerProps } from './TwoColumnBanner.types'
import { BrandIcon } from '../../../../Compounds/src/BrandIconCompound'

const renderButton = (
  btnText?: string,
  btnUrl?: string,
  btnTarget?: string,
  btnAnalytics?: string,
  btnType?: string,
  btnColor?: string
) => {
  if (!btnUrl) return null
  return (
    <a
      className={`Button Button--${btnType} ${btnColor === 'white' ? 'Button--reversed' : ''} ${
        btnType === 'primary' ? 'u-marginRight2gu u-sm-marginRight0 u-sm-marginBottom2gu' : ''
      }`}
      href={btnUrl}
      target={btnTarget}
      data-analytics={btnAnalytics}>
      {btnText}
    </a>
  )
}

const renderColumn = (columnProps: any) => {
  const {
    testId,
    icon,
    iconColor,
    iconSize,
    iconIdentifier,
    iconStyles,
    svgUrl,
    eyebrowImage,
    eyebrowImageAlt,
    eyebrowText,
    columnContent,
    hasImage,
    primaryBtn,
    tertiaryBtn,
    columnClasses,
  } = columnProps

  return (
    <div
      className={`TwoColumnBanner-column u-lg-size6of12 u-sm-sizeFull u-paddingVert0 u-paddingHoriz0 ${columnClasses}`}
      data-testid={testId ? `${testId}-columnSection` : null}>
      <div className={`${hasImage ? 'TwoColumnBanner-fullHeight' : ''}`}>
        {icon && (
          <div className='u-paddingBottom2gu'>
            <BrandIcon
              iconsDropdown={icon}
              iconsColorsDropdown={iconColor}
              size={iconSize}
              identifier={iconIdentifier}
              stylesDropdown={iconStyles}
            />
          </div>
        )}
        {eyebrowImage && (
          <div className='u-paddingBottom2gu'>
            <img src={eyebrowImage} alt={eyebrowImageAlt} />
          </div>
        )}
        {eyebrowText && <p className='Type-5 u-textEyebrow u-paddingVert2gu'>{eyebrowText}</p>}
        <div
          className={`TwoColumnBanner-innerContent ${hasImage ? 'TwoColumnBanner-fullHeight' : ''}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(columnContent) }}
        />
        {(primaryBtn || tertiaryBtn) && (
          <div className='u-flex u-sm-flexCol u-paddingTop6gu'>
            {primaryBtn || null}
            {tertiaryBtn || null}
          </div>
        )}
      </div>
    </div>
  )
}

const getColumnClasses = (
  column?: string,
  leftContentHasImage?: boolean,
  rightContentHasImage?: boolean
) => {
  let defaultClasses = `u-sm-paddingHoriz3gu u-md-paddingHoriz4gu `
  let contentHasImage = `u-sm-flexOrderFirst u-md-flexOrderFirst u-sm-paddingBottom8gu u-md-paddingBottom8gu u-flexAlignContentCenter`
  let contentHasNoImage = `u-paddingRight8gu u-sm-paddingBottom8gu u-md-paddingBottom8gu u-flexAlignContentStart`

  if (column === 'left') {
    if (leftContentHasImage) return defaultClasses + contentHasImage
    if (!leftContentHasImage && rightContentHasImage)
      return defaultClasses + 'u-flexAlignContentCenter u-paddingRight8gu'
    if (!leftContentHasImage && !rightContentHasImage) return defaultClasses + contentHasNoImage
  }
  if (column === 'right') {
    if (rightContentHasImage) return defaultClasses + contentHasImage
    if (!rightContentHasImage && leftContentHasImage)
      return defaultClasses + 'u-flexAlignContentCenter u-paddingLeft8gu'
  }
  return defaultClasses.trim()
}

const isBgDark = (color: string) => {
  return ['purple', 'midnight-blue'].includes(color)
}

const getColorClasses = (bgColor: any, textColor: any) => {
  let bgClass = ''
  let isDark
  if (bgColor) {
    bgClass = getValueListBgColor(bgColor)
    isDark = isBgDark(bgColor)
  }
  const contrastClass = textColor === 'white' || isDark ? 'Content--reversed' : ''
  return `${bgClass} ${contrastClass}`
}

export const TwoColumnBanner: FC<TwoColumnBannerProps> = props => {
  const env = getCWPEnvironmentForURL(window.location.href)
  const galleryPath = APIENDPOINT(env).baseGallery
  const svg = `${galleryPath}content/gallery/ampmicrosites/brand-icons`
  const hasFacet = props?.content?.bgColor?.includes('facet')

  const { content, testId } = props
  const {
    verticalSpacing,
    leftIcon: {
      iconsDropdown: leftIcon,
      iconsColorsDropdown: leftIconColor,
      stylesDropdown: leftIconStyles,
      size: leftIconSize,
      identifier: leftIconIdentifier,
      enableBorder: leftIconEnableBorder = content.leftIcon?.enableBorder === 'true',
    } = {},
    leftEyebrow: { alt: leftEyebrowImageAlt, src: leftEyebrowImage } = {},
    leftEyebrowText,
    leftColumn,
    leftLink,
    svgUrl = svg,
    rightIcon: {
      iconsDropdown: rightIcon,
      iconsColorsDropdown: rightIconColor,
      size: rightIconSize,
      identifier: rightIconIdentifier,
      stylesDropdown: rightIconStyles,
      enableBorder: rightIconEnableBorder = content.leftIcon?.enableBorder === 'true',
    } = {},
    rightEyebrow: { src: rightEyebrowImage, alt: rightEyebrowImageAlt } = {},
    rightEyebrowText,
    rightColumn,
    rightLink,
    btnColor,
    brandExtension,
  } = content

  const backgroundImage =
    content?.bgColor && hasFacet
      ? `${galleryPath}content/gallery/ampmicrosites/facets/banner-facet-${
          props?.content?.bgColor?.replace('-facet', '') || 'midnightblue'
        }.svg`
      : ''
  const backgroundColor = props.content?.bgColor && !hasFacet ? props.content.bgColor : ''
  const textColor = props.content?.bgTheme === 'dark' ? 'white' : 'black'

  const {
    text: leftPrimaryBtnText,
    href: leftPrimaryBtnUrl,
    target: leftPrimaryBtnTarget,
    analytics: leftPrimaryBtnAnalytics,
  } = leftLink?.[0] || {}
  const {
    text: leftTertiaryBtnText,
    href: leftTertiaryBtnUrl,
    target: leftTertiaryBtnTarget,
    analytics: leftTertiaryBtnAnalytics,
  } = leftLink?.[1] || {}

  const {
    text: rightPrimaryBtnText,
    href: rightPrimaryBtnUrl,
    target: rightPrimaryBtnTarget,
    analytics: rightPrimaryBtnAnalytics,
  } = rightLink?.[0] || {}
  const {
    text: rightTertiaryBtnText,
    href: rightTertiaryBtnUrl,
    target: rightTertiaryBtnTarget,
    analytics: rightTertiaryBtnAnalytics,
  } = rightLink?.[1] || {}

  const leftContentHasImage = leftColumn?.content?.includes('<img')
  const rightContentHasImage = rightColumn?.content?.includes('<img')

  const leftPrimaryBtn = renderButton(
    leftPrimaryBtnText,
    leftPrimaryBtnUrl,
    leftPrimaryBtnTarget,
    leftPrimaryBtnAnalytics,
    'primary',
    btnColor
  )
  const leftTertiaryBtn = renderButton(
    leftTertiaryBtnText,
    leftTertiaryBtnUrl,
    leftTertiaryBtnTarget,
    leftTertiaryBtnAnalytics,
    'tertiary',
    btnColor
  )
  const rightPrimaryBtn = renderButton(
    rightPrimaryBtnText,
    rightPrimaryBtnUrl,
    rightPrimaryBtnTarget,
    rightPrimaryBtnAnalytics,
    'primary',
    btnColor
  )
  const rightTertiaryBtn = renderButton(
    rightTertiaryBtnText,
    rightTertiaryBtnUrl,
    rightTertiaryBtnTarget,
    rightTertiaryBtnAnalytics,
    'tertiary',
    btnColor
  )

  return (
    <section
      className={`TwoColumnBanner u-flex u-flexAlignItemsStretch u-flexAlignItemsCenter ${getColorClasses(
        backgroundColor,
        textColor
      )} ${verticalSpacing} ${brandExtension}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
      }}
      data-testid={testId ? `${testId}-backgroundImage` : null}>
      <div className='Content Grid u-paddingVert8gu u-sm-paddingVert6gu u-md-paddingVert6gu u-sizeConstrained'>
        {renderColumn({
          testId: testId ? `${testId}-left` : null,
          icon: leftIcon,
          iconColor: leftIconColor,
          iconSize: leftIconSize,
          iconIdentifier: leftIconIdentifier,
          iconStyles: leftIconStyles,
          svgUrl: svgUrl,
          eyebrowImage: leftEyebrowImage,
          eyebrowImageAlt: leftEyebrowImageAlt,
          eyebrowText: leftEyebrowText,
          columnContent: leftColumn?.content,
          hasImage: leftContentHasImage,
          primaryBtn: leftPrimaryBtn,
          tertiaryBtn: leftTertiaryBtn,
          columnClasses: getColumnClasses('left', leftContentHasImage, rightContentHasImage),
        })}
        {renderColumn({
          testId: testId ? `${testId}-right` : null,
          icon: rightIcon,
          iconColor: rightIconColor,
          iconSize: rightIconSize,
          iconIdentifier: rightIconIdentifier,
          iconStyles: rightIconStyles,
          svgUrl: svgUrl,
          eyebrowImage: rightEyebrowImage,
          eyebrowImageAlt: rightEyebrowImageAlt,
          eyebrowText: rightEyebrowText,
          columnContent: rightColumn?.content,
          hasImage: rightContentHasImage,
          primaryBtn: rightPrimaryBtn,
          tertiaryBtn: rightTertiaryBtn,
          columnClasses: getColumnClasses('right', leftContentHasImage, rightContentHasImage),
        })}
      </div>
    </section>
  )
}
